import { Button, Paper, TableContainer } from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataGridTable } from '../../components/core/DataGridTable/DataGridTable';
import { AssetDashboardSetting } from '../../types/app/asset.types';
import { PlannedQty, PlannedQtyUpdateRequest } from '../../types/app/plannedQty.types';
import moment, { unitOfTime } from 'moment';
import { GridValueFormatterParams } from '@mui/x-data-grid';
import { convertGranularityToMinutes } from '../../utils/core/functions/convertDateTime';
import {
  dateTimeShowFormat,
  hoursMinutesShowFormat,
  hoursMinutesSecondsShowFormat,
} from '../../types/core/date.types';
import { Timezone } from '../../components/app/TimezoneChanger/TimezoneChanger';
import { useTranslation } from '../../contexts/translation';
import { useQueryClient } from 'react-query';
interface Props {
  data: PlannedQty[];
  timezone: Timezone;
  settingData: AssetDashboardSetting;
  onCreateClick?: any;
  onEditClick?: any;
  onDeleteClick?: (id: number) => void;
  readOnly?: boolean;
  mutateUpdatePlannedQty: any;
  mutateCreatePlannedQty: any;
  mutateDeletePlannedQty: any;
}

export const generateTimeData = (
  id: number,
  data: PlannedQty[],
  settingData: AssetDashboardSetting,
) => {
  let newData: PlannedQty[] = [];
  const startOfDay = moment().startOf('day').utc();
  const endOfDay = startOfDay.clone().add(1, 'day');
  let currDate = startOfDay.clone();
  let index = 0;
  // From start to end, step is granularity in minutes
  while (
    currDate
      .add(convertGranularityToMinutes(settingData.timeAggregation), 'minutes')
      .diff(endOfDay) <= 0
  ) {
    const timeFromData = data.filter(
      (x) => x.time === currDate.clone().utc().format(hoursMinutesSecondsShowFormat),
    );
    if (timeFromData.length > 0) {
      newData = [...newData, { ...timeFromData[0], time: currDate.clone() }];
      continue;
    }

    newData = [
      ...newData,
      {
        time: currDate.clone(),
        id: 'tmp' + index,
        assetId: id,
        qty: 0,
        isTmp: true,
      },
    ];
    index++;
  }
  return newData;
};

export function PlannedQtyListDataProcess({
  data,
  timezone,
  settingData,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  readOnly,
  mutateUpdatePlannedQty,
  mutateCreatePlannedQty,
  mutateDeletePlannedQty,
}: Props) {
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const columns = [
    {
      headerName: t('page.dashboard.planned-qty.table-col-1'),
      field: 'time',
      editable: false,
      width: 120,
      renderCell: (data: any) => {
        const time = data.value.clone().tz(timezone.value);
        return `${time
          .clone()
          .add(-convertGranularityToMinutes(settingData.timeAggregation), 'minute')
          .format(hoursMinutesShowFormat)} - ${time.format(hoursMinutesShowFormat)}`;
      },
    },
    {
      headerName: t('page.dashboard.planned-qty.table-col-2'),
      field: 'qty',
      editable: !readOnly,
      width: 120,
    },
    {
      headerName: t('page.dashboard.planned-qty.table-col-3'),
      field: 'updated',
      valueFormatter: (params: GridValueFormatterParams) => {
        return params.value
          ? moment(params.value?.toString()).local().format(dateTimeShowFormat)
          : '';
      },
      width: 150,
    },
  ];
  const handleCopyFirstValue = () => {
    const tmpData = generateTimeData(Number(id), data, settingData);
    tmpData.map(async (item: PlannedQty, index: number) => {
      if (index === 0) return;
      let isGenerated = typeof item.id === 'string';

      if (tmpData[0].qty === 0) {
        if (item.qty !== 0)
          mutateDeletePlannedQty(item.id, {
            onSuccess: () => {
              queryClient.invalidateQueries(['plannedQty']);
            },
          });
      } else {
        const requestData = {
          assetId: item.assetId,
          id: isGenerated ? undefined : item.id,
          qty: tmpData[0].qty,
          time: moment(item.time.utc(), hoursMinutesSecondsShowFormat).format(
            hoursMinutesSecondsShowFormat,
          ),
        };
        if (item && isGenerated) {
          mutateCreatePlannedQty(
            { ...requestData },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(['plannedQty']);
              },
            },
          );
        } else {
          if (item.qty !== tmpData[0].qty)
            mutateUpdatePlannedQty(
              { ...requestData, id: requestData.id },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(['plannedQty']);
                },
              },
            );
        }
      }
    });
  };
  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          handleCopyFirstValue();
        }}
        style={{ marginBottom: 15 }}
      >
        {t('page.dashboard.planned-qty.copy-first-value')}
      </Button>
      <TableContainer component={Paper}>
        <DataGridTable
          data={generateTimeData(Number(id), data, settingData)}
          columns={columns}
          height={1000}
          pageSize={100}
          onCreateClick={onCreateClick}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          readOnly={readOnly}
        />
      </TableContainer>
    </>
  );
}
